import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridServerFilterMap, TransactionStatusCode } from '@tassat/common-definitions';
import { CollateralHistoryViewRecordExtended } from '@tassat/fred-extended-definitions';
import React from 'react';

// Cell Renderers
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { auditTimeSort } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole, getUser } from 'Selectors/common';
import { adminPledgesMapping, clientPledgesMapping } from 'Selectors/pledges';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import AdminFirmWalletCellRenderer from 'SharedComponents/cellRenderComponent/adminFirmWalletCellRenderer';
import ClientCounterpartyWalletCellRenderer from 'SharedComponents/cellRenderComponent/clientCounterpartyCellRenderer';
import ClientCounterpartyFirmCellRenderer from 'SharedComponents/cellRenderComponent/clientCounterpartyFirmCellRenderer';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import DirectionCellRender from 'SharedComponents/cellRenderComponent/directionCellRender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';
import FormatAssetsAmount from 'SharedComponents/cellRenderComponent/formatAssetsAmount';
import FormatCurrentBalance from 'SharedComponents/cellRenderComponent/formatCurrentBalance';
import MultiLineCellRenderer from 'SharedComponents/cellRenderComponent/multiLineCellRenderer';
import { PledgeStatusCellRenderer } from 'SharedComponents/cellRenderComponent/pledgeStatusCellRenderer';
import { TextCellRender } from 'SharedComponents/cellRenderComponent/textCellrender';
import UserIdCellRenderer from 'SharedComponents/cellRenderComponent/userIdCellRenderer';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import {
    getAdminCollateralHistoryColumnDefs,
    getClientCollateralHistoryColumnDefs,
    getMappedColumn
} from './collateralHistoryColumnDefs';

import type { CollateralHistoryAgGridProps } from '.';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';

export function CollateralHistoryAgGrid (props: CollateralHistoryAgGridProps) {
    const {
        tableOptions,
        setSelectedPledgeId,
        overlayOpen
    } = props;
    const currentUser = useAppSelector(getUser);
    const pledges = useAppSelector((state) => (currentUser?.IsAdmin ? adminPledgesMapping(state) : clientPledgesMapping(state)));
    const currentRole = useAppSelector(getCurrentRole);

    const [features] = useFeatures();
    const [ gridApi, onGridReady ] = useAgGrid();
    const [ sortedPledges, setSortedPledges ] =
    React.useState<CollateralHistoryViewRecordExtended[]>(pledges);

    React.useEffect(() => {
        if (gridApi) {
            gridApi.api.onFilterChanged();
        }
    }, [ gridApi, tableOptions.TableFilters.length, tableOptions.Sort, tableOptions.DefaultState ]);

    React.useEffect(() => {
        let mappedPledges = pledges;
        const dateConditionArray = tableOptions.DateCondition ?
            tableOptions.DateCondition.split(':')
            : [];

        const dateArray = dateConditionArray.map((date) => {
            const dateSplit = date.split('|');

            return dateSplit;
        });

        if (dateArray.length) {
            mappedPledges = mappedPledges.filter(
                (pledge) => {
                    let date;
                    for (let i = 0; i < dateArray.length; i++) {
                        const dateFilter = dateArray[i];
                        if (dateFilter[1] === AgGridServerFilterMap.greaterThan) {
                            date = pledge.AuditTime >= dateFilter[2];
                            if (!date) {
                                break;
                            }
                        }
                    }

                    return date;
                }
            );
        }

        if (tableOptions.Sort === 'AuditTime|DESC') {
            mappedPledges.sort(auditTimeSort);
        }

        setSortedPledges(mappedPledges);
    }, [
        pledges,
        tableOptions.DateCondition,
        tableOptions.Sort,
        tableOptions.SelectedFirmName,
        tableOptions.SelectedAccount,
        tableOptions.SelectedFirm
    ]);

    const selectPledge = (e) => {
        const { data } = e;
        setSelectedPledgeId(`${data.PledgeId}`);
    };

    const columnDefs = React.useMemo(() => {
        if (currentUser?.IsAdmin) {
            return getAdminCollateralHistoryColumnDefs({
                features,
                currentUser,
                currentRole,
                tableOptions,
                getMappedColumn
            });
        }

        return getClientCollateralHistoryColumnDefs({
            features,
            currentUser,
            currentRole,
            tableOptions,
            getMappedColumn
        });
    }, [
        features,
        currentUser,
        currentRole,
        tableOptions
        // getBalance
    ]);

    return (
        <div
            className='ag-theme-material'
            id='myGrid'
            style={{
                minWidth: '100%',
                height: '100%'
            }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={columnDefs}
                columnMenu='legacy'
                components={{
                    externalFilterHeader: ExternalFilterHeaderComponent,
                    directionCellRender: DirectionCellRender,
                    dateCellRender: DateCellRender,
                    formatAssetsAmount: FormatAssetsAmount,
                    tranStatus: PledgeStatusCellRenderer,
                    counterpartyWallet: ClientCounterpartyWalletCellRenderer,
                    formatCurrentBalance: FormatCurrentBalance,
                    counterpartyFirm: ClientCounterpartyFirmCellRenderer,
                    firmWallet: AdminFirmWalletCellRenderer,
                    multiLineText: MultiLineCellRenderer,
                    textCell: TextCellRender,
                    userIdCell: UserIdCellRenderer,
                    externalFilter: ExternalFilter
                }}
                defaultColDef={{
                    resizable: true,
                    sortable: false,
                    minWidth: 80
                }}
                getRowId={({ data }) => `${data.PledgeId}`}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onGridReady={onGridReady}
                onRowClicked={selectPledge}
                rowClassRules={{
                    'lockedRow': ({ data }) => (
                        [
                            TransactionStatusCode.FAILED,
                            TransactionStatusCode.REJECTED
                        ].includes(data.Status)
                    ),
                    'pendingRow': ({ data }) => data.Status === TransactionStatusCode.PENDING_APPROVAL
                }}
                rowData={sortedPledges}
                rowHeight={currentUser?.IsAdmin ? 60 : undefined}
                suppressCellFocus={true}
                suppressContextMenu={true}
                suppressNoRowsOverlay={overlayOpen}
            />
        </div>
    );
}